/**
 * Instalar Faktelio como aplicación — `OD-0065` (ACCEPTED 2026-10-25, D-01 A:
 * «Solo web instalable»).
 *
 * No hay programa aparte: es la misma PWA de `ADR-0004`. Edge y Chrome la
 * instalan con su propio diálogo, y este fichero solo decide qué se le dice
 * al usuario en Configuración › Mi cuenta:
 *
 *   instalada   ya va en su propia ventana (display-mode standalone)
 *   boton       el navegador ofrece instalar (`beforeinstallprompt`): un botón
 *   safari-mac  Safari en Mac no lanza el evento: Archivo › Añadir al Dock
 *   ios         iPhone o iPad: Compartir › Añadir a pantalla de inicio
 *   otro        cualquier otro caso: abrirla en Edge o Chrome
 *
 * `beforeinstallprompt` llega UNA vez y puede llegar antes de que React pinte
 * nada, así que se escucha desde `main.tsx` y se guarda aquí.
 */
import { useSyncExternalStore } from "react";

export type ComoInstalar = "instalada" | "boton" | "safari-mac" | "ios" | "otro";

/** Lo que el navegador entrega con `beforeinstallprompt` (no está en lib.dom). */
type EventoInstalar = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/** La regla, sin `window`: es lo que prueba `tests/adversarial/instalar-app.mjs`. */
export function comoInstalar(entorno: {
  userAgent: string;
  maxTouchPoints: number;
  standalone: boolean;
  hayPrompt: boolean;
}): ComoInstalar {
  if (entorno.standalone) return "instalada";
  if (entorno.hayPrompt) return "boton";
  const ua = entorno.userAgent;
  // Un iPad con iPadOS se presenta como «Macintosh»: lo delata la pantalla táctil.
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && entorno.maxTouchPoints > 1)) return "ios";
  if (/Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|Firefox\/|OPR\//.test(ua)) {
    return "safari-mac";
  }
  return "otro";
}

let pendiente: EventoInstalar | null = null;
let instalada = false;
const avisar = new Set<() => void>();

function cambio(): void {
  for (const f of avisar) f();
}

/** Desde `main.tsx`, antes de pintar: el evento no se repite. */
export function escucharInstalacion(): void {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Sin esto Chrome enseña su propia barra; el botón de Mi cuenta la sustituye.
    e.preventDefault();
    pendiente = e as EventoInstalar;
    cambio();
  });
  window.addEventListener("appinstalled", () => {
    pendiente = null;
    instalada = true;
    cambio();
  });
}

function estado(): ComoInstalar {
  const standalone =
    instalada ||
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return comoInstalar({
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
    standalone,
    hayPrompt: pendiente !== null,
  });
}

function suscribir(f: () => void): () => void {
  avisar.add(f);
  return () => avisar.delete(f);
}

export function useComoInstalar(): ComoInstalar {
  return useSyncExternalStore(suscribir, estado);
}

/** Abre el diálogo del navegador. El evento solo sirve una vez, acepte o no. */
export async function pedirInstalacion(): Promise<void> {
  const e = pendiente;
  if (e === null) return;
  pendiente = null;
  await e.prompt();
  const { outcome } = await e.userChoice;
  if (outcome === "accepted") instalada = true;
  cambio();
}
